import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { caseStudies } from "@/data/caseStudies";

const deployments = [
  { x: "44%", y: "44%", slug: "ghana", label: "GOCEMS · Ghana" },
  { x: "57%", y: "55%", slug: "kenya", label: "KOCEMS · Kenya" },
  { x: "55%", y: "62%", slug: "tanzania", label: "TOCEMS · Tanzania" },
  { x: "40%", y: "42%", slug: "nigeria", label: "NIMACEMS · Nigeria" },
];

function HeroCaseStudies() {
  const { t } = useTranslation();
  return (
    <section className="relative pt-32 pb-xl overflow-hidden">
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-cream via-background to-surface-container-low" />

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-lg items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="font-label-caps text-on-tertiary-container bg-tertiary-fixed px-3 py-1 rounded-full mb-md inline-block">
              {t("case_studies.hero.eyebrow")}
            </span>
            <h1 className="font-h1 text-[40px] md:text-h1 text-primary mb-md">
              {t("case_studies.hero.title_a")} <span className="text-gradient">{t("case_studies.hero.title_accent")}</span>
            </h1>
            <p className="text-body-lg text-on-surface-variant max-w-xl">
              {t("case_studies.hero.intro")}
            </p>

            <div className="mt-lg flex flex-wrap gap-2">
              {["GOCEMS", "KOCEMS", "TOCEMS", "NIMACEMS"].map((b) => (
                <span key={b} className="px-3 py-1.5 rounded-full bg-white border border-outline-variant text-xs font-bold text-primary">
                  {b}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-primary-container shadow-2xl border border-zinc-800"
          >
            <div className="absolute inset-0 tech-grid-dark opacity-40" />
            <svg viewBox="0 0 100 110" className="absolute inset-0 w-full h-full p-6" preserveAspectRatio="xMidYMid meet">
              <motion.path
                initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }} transition={{ duration: 2, ease: "easeInOut" }}
                d="M48,8 C58,8 66,12 70,18 C74,22 76,28 78,34 C80,42 82,50 80,58 C78,66 74,72 72,80 C70,88 66,94 62,98 C58,102 52,104 48,102 C44,100 40,96 38,90 C34,84 30,78 28,70 C26,62 22,56 22,48 C22,40 26,32 32,24 C36,16 42,8 48,8 Z"
                fill="rgba(144,184,73,0.08)" stroke="rgba(144,184,73,0.6)" strokeWidth="0.4"
              />
            </svg>

            {deployments.map((h, i) => (
              <motion.div
                key={h.slug}
                initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.6 + i * 0.15 }}
                style={{ left: h.x, top: h.y }}
                className="absolute -translate-x-1/2 -translate-y-1/2 group"
              >
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-brand/40 animate-ping-slow" />
                  <div className="relative w-3 h-3 rounded-full bg-brand shadow-[0_0_12px_rgba(144,184,73,0.9)]" />
                </div>
                <div className="absolute left-5 top-1/2 -translate-y-1/2 whitespace-nowrap glass-dark px-2 py-1 rounded-md text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  {h.label}
                </div>
              </motion.div>
            ))}

            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-zinc-300">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest">
                <span className="w-2 h-2 rounded-full bg-brand animate-pulse" />
                {t("case_studies.hero.africa_first")}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-brand font-mono">AOCEMS · v4.2</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function CaseList() {
  const { t } = useTranslation();
  return (
    <section className="max-w-[1280px] mx-auto px-6 py-xl">
      <div className="space-y-lg">
        {caseStudies.map((c, i) => {
          const headline = t(`case_studies.items.${c.slug}.headline`, c.headline);
          const summary = t(`case_studies.items.${c.slug}.listSummary`, c.listSummary);
          const tag = t(`case_studies.tags.${c.tag}`, c.tag);
          const presentationTitle = t(`case_studies.items.${c.slug}.presentationTitle`, c.presentation?.title);
          return (
            <motion.article
              key={c.slug}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, delay: i * 0.06 }}
              className={`grid md:grid-cols-2 gap-md items-center ${i % 2 ? "md:[&>div:first-child]:order-2" : ""}`}
            >
              <div className="rounded-2xl overflow-hidden aspect-[4/3] shadow-xl border border-outline-variant bg-white flex items-center justify-center p-md">
                <img src={c.logo} alt={`${c.code} logo`} className="max-h-full max-w-full object-contain" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="px-3 py-1 bg-tertiary-fixed text-on-tertiary-fixed-variant rounded-full text-[10px] font-bold uppercase tracking-widest">
                    {tag}
                  </span>
                  <span className="px-3 py-1 bg-primary-container text-brand rounded-full text-[10px] font-bold uppercase tracking-widest">
                    {c.code}
                  </span>
                  <span className="text-xs text-on-surface-variant flex items-center gap-1">
                    <span className="material-symbols-outlined text-[14px]">location_on</span>
                    {c.country}
                  </span>
                </div>
                <h3 className="font-h2 text-[28px] leading-tight text-primary mb-3">{headline}</h3>
                <p className="text-on-surface-variant mb-md">{summary}</p>
                <div className="flex flex-wrap items-center gap-4">
                  <Link to={`/case-studies/${c.slug}`} data-testid={`case-link-${c.slug}`} className="inline-flex items-center gap-2 text-brand font-bold hover:gap-3 transition-all">
                    {t("case_studies.list.read_full")} <span className="material-symbols-outlined">arrow_forward</span>
                  </Link>
                  {c.presentation && (
                    <a
                      href={`/docs/${c.presentation.file}`}
                      download={c.presentation.file}
                      data-testid={`case-presentation-list-${c.slug}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-outline-variant text-sm font-bold text-primary hover:border-brand hover:text-brand transition-all"
                      title={presentationTitle}
                    >
                      <span className="material-symbols-outlined text-[18px]">slideshow</span>
                      {t("case_studies.list.programme_deck")}
                    </a>
                  )}
                </div>
              </div>
            </motion.article>
          );
        })}
      </div>
    </section>
  );
}

export default function CaseStudies() {
  return (
    <>
      <HeroCaseStudies />
      <CaseList />
    </>
  );
}
